"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { SEUIL_DIVIDENDES_EURL, comparerFormes } from "@/lib/tools/salaryDividends";
import { expliquerPartage } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * EURL ou SASU : laquelle me laisse le plus, et ce que l'écart me coûte.
 *
 * LA COMPARAISON QUE TOUT LE MONDE FAIT DE TRAVERS. On oppose une SASU payée
 * tout en dividendes à une EURL qui cotise sur tout ce qu'elle verse : la SASU
 * gagne, et l'on conclut qu'elle est « plus avantageuse ». C'est comparer une
 * année sans retraite à une année avec. L'écran compare les deux à trimestres
 * de retraite égaux, et montre à côté le tout-dividendes avec son prix.
 *
 * CE QU'ON TROUVE, ET QUE L'ÉCRAN DIT SANS DÉTOUR : à trimestres égaux, les
 * deux formes se tiennent à quelques centaines d'euros sur l'essentiel des
 * bénéfices d'un solopreneur. La vraie différence est ce que chacune verse aux
 * caisses de retraite, et donc le montant de la pension. L'écran l'affiche
 * pour chacune, en euros par an.
 *
 * UNE COURBE POUR LA QUESTION « QUAND » : l'écart selon le bénéfice, de 10 000
 * à 200 000 €. Il change de signe plusieurs fois ; un seuil unique de passage
 * en SASU serait donc faux, et l'écran n'en invente pas.
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    activite: 0,
    cipav: 0,
    enveloppe: 1,
    capital: 1,
    foyer: 2,
    autresRevenus: 2,
    resultat: 3,
};

/** Les bénéfices de la courbe : de 10 000 à 200 000 €, de 5 000 en 5 000. */
const PAS_COURBE = 5000;
const COURBE_DE = 10000;
const COURBE_A = 200000;

const L = {
    fr: {
        phases: ["Activité", "Chiffres", "Foyer"],
        retour: "Retour",
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "En EURL, elle change une partie de vos cotisations d'indépendant.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Commerce ou prestations",
                    detail: "Vous vendez, ou vous rendez des services à des entreprises ou à des particuliers.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Artisanat",
                    detail: "Vous êtes, ou vous serez, immatriculé comme artisan.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation, rédaction.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Profession réglementée",
                    detail: "Architecte, psychologue, ostéopathe : vous cotisez à la Cipav.",
                },
            ],
        },
        cipav: {
            titre: "Pour la Cipav, nous n'avons pas encore le calcul",
            aide: "Gérant d'EURL affilié à la Cipav, vous cotiseriez pour votre retraite selon un barème à part, que nous ne savons pas encore calculer au centime. Plutôt que de comparer sur un chiffre faux, nous préférons nous arrêter ici.",
            points: [
                "En SASU, vous relèveriez du régime général, comme n'importe quel cadre.",
                "Votre expert-comptable peut chiffrer l'EURL avec les taux de la Cipav.",
            ],
        },
        enveloppe: {
            titre: "Combien votre société gagne-t-elle, ou gagnera-t-elle, avant de vous payer ?",
            aide: "C'est son chiffre d'affaires moins ses dépenses, avant votre rémunération et vos cotisations.",
            libelle: "Bénéfice avant votre rémunération",
            suivant: "Continuer",
        },
        capital: {
            titre: "Combien y mettez-vous ?",
            aide: "Comptez le capital, les primes d'émission, et l'argent laissé sur votre compte courant d'associé. En EURL, vos dividendes paient des cotisations au-delà de {seuil} de ce montant.",
            libelle: "Capital, primes et compte courant",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "Ce que votre société vous verse s'ajoute aux revenus de votre foyer, et votre impôt est calculé sur l'ensemble.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Continuer",
        },
        autresRevenus: {
            titre: "Y a-t-il d'autres revenus dans votre foyer ?",
            aide: "Un salaire, une pension, les revenus de votre conjoint. Nous ne comptons que l'impôt en plus que votre société vous fait payer.",
            libelle: "Autres revenus imposables du foyer",
            aucun: "Aucun autre revenu",
            suivant: "Comparer l'EURL et la SASU",
        },
        resultat: {
            titre: "EURL ou SASU",
            sasuDevant: "La SASU vous laisse {gain} de plus par an",
            eurlDevant: "L'EURL vous laisse {gain} de plus par an",
            egalite: "Côté argent, les deux se valent",
            contexte: {
                aucun: "Sans trimestre de retraite ni d'un côté ni de l'autre",
                un: "Avec un trimestre de retraite validé de chaque côté",
                plusieurs: "Avec {n} trimestres de retraite validés de chaque côté",
            },
            detail: "{contexte}, l'EURL vous laisse {eurl} et la SASU {sasu}.",
            detailEgalite:
                "{contexte}, l'EURL vous laisse {eurl} et la SASU {sasu}. Moins de mille euros d'écart, c'est moins que la marge d'erreur de ce calcul.",
            retraite:
                "Côté retraite, l'EURL verse {eurl} par an à vos caisses, la SASU {sasu}. Vos trimestres décident de l'âge où vous partez à taux plein, mais le montant de votre pension dépend de ce que vous avez cotisé.",
            retraiteProche:
                "Et les deux versent à peu près autant à vos caisses de retraite : {eurl} par an pour l'EURL, {sasu} pour la SASU.",
            cartes: {
                eurl: "EURL",
                sasu: "SASU",
                reste: "Il vous reste",
                parAn: "par an",
                toutRemuneration: "Tout en rémunération",
                mixte: "{remuneration} de rémunération, le reste en dividendes",
                salaire: "{brut} de salaire brut, le reste en dividendes",
                trimestres: {
                    aucun: "Aucun trimestre de retraite validé",
                    un: "Un trimestre de retraite validé",
                    plusieurs: "{n} trimestres de retraite validés",
                },
                caisses: "{montant} versés à vos caisses de retraite",
            },
            sansSalaire:
                "Si vous preniez tout en dividendes, la SASU vous laisserait {net}, soit {ecart} {sens} que l'EURL. Mais vous ne valideriez aucun trimestre, et vous ne cotiseriez pas du tout pour votre retraite.",
            plus: "de plus",
            moins: "de moins",
            courbe: {
                titre: "L'écart selon votre bénéfice",
                aide: "Au-dessus de la ligne, la SASU vous laisse plus ; en dessous, c'est l'EURL. Toujours avec autant de trimestres de chaque côté. La barre en couleur, c'est votre bénéfice.",
                haut: "SASU devant",
                bas: "EURL devant",
            },
            autres: {
                titre: "Ce qui change aussi",
                items: [
                    "En EURL, vous payez vos cotisations à l'avance, sur vos revenus passés, puis l'Urssaf les ajuste une fois vos vrais revenus déclarés. En SASU, elles partent avec chaque salaire.",
                    "Dans aucune des deux formes vous n'avez droit au chômage : ni le gérant d'EURL ni le président de SASU ne cotisent pour lui.",
                ],
            },
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "chaque forme vous paie selon son meilleur partage entre rémunération et dividendes, et tout le bénéfice restant vous est versé dans l'année ;",
                    "la SASU vous verse juste assez de salaire pour valider autant de trimestres que l'EURL ;",
                    "certaines charges ne sont pas comptées : en SASU, la cotisation accidents du travail ; et dans les deux, les honoraires de l'expert-comptable ;",
                    "l'impôt compté est celui que votre société ajoute à votre foyer, pas tout l'impôt du foyer.",
                ],
            },
            avertissement:
                "Ces montants sont indicatifs. Choisissez votre forme avec un expert-comptable : elle engage votre protection sociale pour des années.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Activity", "Figures", "Household"],
        retour: "Back",
        activite: {
            titre: "What is your activity?",
            aide: "In an EURL, it changes part of your self-employed contributions.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Trade or services",
                    detail: "You sell goods, or provide services to businesses or individuals.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Craft trade",
                    detail: "You are, or will be, registered as a craftsperson.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training, writing.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Regulated profession",
                    detail: "Architect, psychologist, osteopath: you contribute to the Cipav.",
                },
            ],
        },
        cipav: {
            titre: "We do not have the Cipav calculation yet",
            aide: "As an EURL manager in the Cipav, your pension contributions would follow their own scale, which we cannot yet compute to the cent. Rather than compare on a wrong figure, we prefer to stop here.",
            points: [
                "In a SASU, you would fall under the general scheme, like any executive.",
                "Your accountant can cost the EURL with the Cipav rates.",
            ],
        },
        enveloppe: {
            titre: "How much does, or will, your company make before paying you?",
            aide: "That is its turnover minus its expenses, before your pay and your contributions.",
            libelle: "Profit before your pay",
            suivant: "Continue",
        },
        capital: {
            titre: "How much are you putting in?",
            aide: "Count the share capital, share premiums, and money left in your shareholder current account. In an EURL, your dividends carry contributions beyond {seuil} of that amount.",
            libelle: "Capital, premiums and current account",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "What your company pays you adds to your household's income, and your tax is calculated on the whole.",
            situation: "Situation",
            seul: "Single",
            couple: "Couple",
            enfants: "Dependent children",
            suivant: "Continue",
        },
        autresRevenus: {
            titre: "Is there other income in your household?",
            aide: "A salary, a pension, your partner's income. We only count the extra tax your company makes you pay.",
            libelle: "Other taxable household income",
            aucun: "No other income",
            suivant: "Compare the EURL and the SASU",
        },
        resultat: {
            titre: "EURL or SASU",
            sasuDevant: "The SASU leaves you {gain} more a year",
            eurlDevant: "The EURL leaves you {gain} more a year",
            egalite: "On money, the two are even",
            contexte: {
                aucun: "With no pension quarter on either side",
                un: "With one pension quarter validated on each side",
                plusieurs: "With {n} pension quarters validated on each side",
            },
            detail: "{contexte}, the EURL leaves you {eurl} and the SASU {sasu}.",
            detailEgalite:
                "{contexte}, the EURL leaves you {eurl} and the SASU {sasu}. Less than a thousand euros apart is less than this calculation's margin of error.",
            retraite:
                "For your pension, the EURL pays in {eurl} a year, the SASU {sasu}. Your quarters decide when you can retire at the full rate, but the amount of your pension depends on what you have contributed.",
            retraiteProche:
                "And both pay roughly the same into your pension funds: {eurl} a year for the EURL, {sasu} for the SASU.",
            cartes: {
                eurl: "EURL",
                sasu: "SASU",
                reste: "You keep",
                parAn: "a year",
                toutRemuneration: "All as pay",
                mixte: "{remuneration} of pay, the rest as dividends",
                salaire: "{brut} of gross salary, the rest as dividends",
                trimestres: {
                    aucun: "No pension quarter validated",
                    un: "One pension quarter validated",
                    plusieurs: "{n} pension quarters validated",
                },
                caisses: "{montant} paid into your pension funds",
            },
            sansSalaire:
                "If you took everything as dividends, the SASU would leave you {net}, that is {ecart} {sens} than the EURL. But you would validate no quarter, and pay nothing at all towards your pension.",
            plus: "more",
            moins: "less",
            courbe: {
                titre: "The gap by profit",
                aide: "Above the line, the SASU leaves you more; below it, the EURL does. Always with as many quarters on each side. The coloured bar is your profit.",
                haut: "SASU ahead",
                bas: "EURL ahead",
            },
            autres: {
                titre: "What else changes",
                items: [
                    "In an EURL, you pay your contributions in advance, based on your past income, then the Urssaf adjusts them once your actual income is declared. In a SASU, they go with each salary payment.",
                    "Neither form gives you unemployment insurance: neither the EURL manager nor the SASU president contributes to it.",
                ],
            },
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "each form pays you according to its best split between pay and dividends, and all remaining profit is paid out to you within the year;",
                    "the SASU pays you just enough salary to validate as many quarters as the EURL;",
                    "some charges are not counted: in a SASU, the workplace accident contribution; and in both, the accountant's fees;",
                    "the tax counted is what your company adds to your household, not the household's whole tax.",
                ],
            },
            avertissement:
                "These amounts are indicative. Choose your form with an accountant: it commits your social cover for years.",
            recommencer: "Start again",
        },
    },
};

/** « aucun », « un » ou « {n} » trimestres : le pluriel ne se règle pas par un « (s) ». */
function selonTrimestres(n, formes) {
    return remplir(n === 0 ? formes.aucun : n === 1 ? formes.un : formes.plusieurs, { n });
}

/**
 * L'écart SASU moins EURL selon le bénéfice, en barres de part et d'autre
 * d'une ligne. Pas de graduation : la courbe dit où l'écart change de signe
 * et combien il reste petit ; le chiffre exact est au-dessus.
 */
function CourbeEcart({ serie, enveloppe, textes, lang }) {
    const L = 320;
    const H = 120;
    const milieu = H / 2;
    const max = Math.max(1000, ...serie.map((p) => Math.abs(p.ecart)));
    const largeur = (L - 16) / serie.length;
    const proche = serie.reduce((a, b) =>
        Math.abs(b.enveloppe - enveloppe) < Math.abs(a.enveloppe - enveloppe) ? b : a,
    );
    return (
        <div className="mt-6">
            <div className="text-sm font-semibold dark:text-white">{textes.titre}</div>
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-2">{textes.aide}</p>
            <svg viewBox={`0 0 ${L} ${H}`} className="w-full h-auto" aria-hidden="true">
                <text x="8" y="12" className="fill-gray-400 text-[9px]">
                    {textes.haut}
                </text>
                <text x="8" y={H - 4} className="fill-gray-400 text-[9px]">
                    {textes.bas}
                </text>
                <line x1="8" x2={L - 8} y1={milieu} y2={milieu} stroke="currentColor" className="text-gray-300 dark:text-white/20" />
                {serie.map((p, i) => {
                    const h = (Math.abs(p.ecart) / max) * (milieu - 16);
                    const x = 8 + i * largeur + 1;
                    return (
                        <rect
                            key={p.enveloppe}
                            x={x}
                            y={p.ecart >= 0 ? milieu - h : milieu}
                            width={Math.max(1, largeur - 2)}
                            height={Math.max(0.5, h)}
                            rx="1"
                            className={
                                p === proche
                                    ? "fill-primary"
                                    : p.ecart >= 0
                                      ? "fill-gray-400 dark:fill-white/40"
                                      : "fill-gray-300 dark:fill-white/20"
                            }
                        />
                    );
                })}
            </svg>
            <div className="flex justify-between text-[11px] text-gray-500 dark:text-lightBlue/70">
                <span>{euros(COURBE_DE, lang)}</span>
                <span>{euros(COURBE_A, lang)}</span>
            </div>
        </div>
    );
}

export default function EurlOuSasu({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La CIPAV n'est jamais une valeur de l'outil : c'est un écran de refus,
    // où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const activiteDossier = dossier.activite !== "cipav" ? dossier.activite : undefined;
    // Un bénéfice sous le minimum du curseur ne se reprend pas : la question reste.
    const enveloppeDossier = dossier.enveloppe >= 5000 ? dossier.enveloppe : undefined;
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [enveloppe, setEnveloppe] = useState(enveloppeDossier ?? 60000);
    const [capital, setCapital] = useState(1000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setEnveloppe(60000);
            setCapital(1000);
            setEnCouple(false);
            setEnfants(0);
            setAutresRevenus(0);
        },
        PHASES,
        reprendre(
            dossier.activite === "cipav"
                ? [{ etape: "activite", connu: true }, { etape: "cipav" }]
                : [
                      { etape: "activite", connu: activiteDossier !== undefined },
                      { etape: "enveloppe", connu: enveloppeDossier !== undefined },
                      // Le capital social ne se déduit pas du dossier : le parcours s'y arrête.
                      { etape: "capital", connu: false },
                      { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
                      { etape: "autresRevenus", connu: dossier.autresRevenus !== undefined },
                      { etape: "resultat" },
                  ],
        ),
    );

    useSignalerDossier({ activite, enCouple, enfants, autresRevenus }, etape === "resultat");

    const parametres = useMemo(
        () => ({
            capital,
            activite: activite === "artisan" ? "services" : activite || "bnc",
            artisan: activite === "artisan",
            foyer: { enCouple, enfants, autresRevenus },
        }),
        [capital, activite, enCouple, enfants, autresRevenus],
    );

    const comparaison = useMemo(
        () => (etape === "resultat" ? comparerFormes({ ...parametres, enveloppe }) : null),
        [etape, parametres, enveloppe],
    );
    const serie = useMemo(() => {
        if (etape !== "resultat") return null;
        const points = [];
        for (let e = COURBE_DE; e <= COURBE_A; e += PAS_COURBE) {
            points.push({ enveloppe: e, ecart: comparerFormes({ ...parametres, enveloppe: e }).ecart });
        }
        return points;
    }, [etape, parametres]);

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    // Un seul calcul déplié à la fois, sous la carte cliquée.
    const { ouverte, basculer } = useExplications();

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "cipav") {
                            aller("cipav");
                            return;
                        }
                        setActivite(c.cle);
                        aller("enveloppe");
                    },
                }))}
            />
        );
    }

    if (etape === "cipav") {
        return (
            <Ecran
                key="cipav"
                {...communsEcran}
                phase={PHASES.cipav}
                rang={rang}
                titre={t.cipav.titre}
                aide={t.cipav.aide}
            >
                <Liste items={t.cipav.points} />
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "enveloppe") {
        return (
            <Ecran
                key="enveloppe"
                {...communsEcran}
                phase={PHASES.enveloppe}
                rang={rang}
                titre={t.enveloppe.titre}
                aide={t.enveloppe.aide}
            >
                <Curseur
                    libelle={t.enveloppe.libelle}
                    valeur={enveloppe}
                    poser={setEnveloppe}
                    minimum={5000}
                    maximum={300000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("capital")}>{t.enveloppe.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "capital") {
        return (
            <Ecran
                key="capital"
                {...communsEcran}
                phase={PHASES.capital}
                rang={rang}
                titre={t.capital.titre}
                aide={remplir(t.capital.aide, { seuil: `${SEUIL_DIVIDENDES_EURL * 100} %` })}
            >
                <Curseur
                    libelle={t.capital.libelle}
                    valeur={capital}
                    poser={setCapital}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.capital.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran
                key="foyer"
                {...communsEcran}
                phase={PHASES.foyer}
                rang={rang}
                titre={t.foyer.titre}
                aide={t.foyer.aide}
            >
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {t.foyer.situation}
                        </div>
                        <Segments
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("autresRevenus")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "autresRevenus") {
        return (
            <Ecran
                key="autresRevenus"
                {...communsEcran}
                phase={PHASES.autresRevenus}
                rang={rang}
                titre={t.autresRevenus.titre}
                aide={t.autresRevenus.aide}
            >
                <Curseur
                    libelle={t.autresRevenus.libelle}
                    aide={autresRevenus === 0 ? t.autresRevenus.aucun : undefined}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    minimum={0}
                    maximum={120000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.autresRevenus.suivant}</Avancer>
            </Ecran>
        );
    }

    const { eurl, sasu, sasuComparable, trimestres, ecart, gagnant, ecartSansSalaire } = comparaison;
    const c = r.cartes;
    const eurlPoint = eurl.meilleur;

    const titre =
        gagnant === "sasu"
            ? remplir(r.sasuDevant, { gain: sommes(ecart) })
            : gagnant === "eurl"
              ? remplir(r.eurlDevant, { gain: sommes(-ecart) })
              : r.egalite;

    const cartes = [
        {
            cle: "eurl",
            nom: c.eurl,
            point: eurlPoint,
            partage:
                eurlPoint.part > 0.995
                    ? c.toutRemuneration
                    : remplir(c.mixte, { remuneration: sommes(eurlPoint.remunerationAvantImpot) }),
        },
        {
            cle: "sasu",
            nom: c.sasu,
            point: sasuComparable,
            partage:
                sasuComparable.part > 0.995
                    ? c.toutRemuneration
                    : remplir(c.salaire, { brut: sommes(sasuComparable.remunerationBrute) }),
        },
    ];
    // Ce que chaque forme laisse dit d'où il vient : du partage jusqu'au net.
    const explications = {
        eurl: expliquerPartage({ ...parametres, enveloppe, forme: "eurl" }, eurlPoint, lang).resume,
        sasu: expliquerPartage({ ...parametres, enveloppe, forme: "sasu" }, sasuComparable, lang).resume,
    };

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{titre}</div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                {remplir(gagnant ? r.detail : r.detailEgalite, {
                    contexte: selonTrimestres(trimestres, r.contexte),
                    eurl: sommes(eurlPoint.net),
                    sasu: sommes(sasuComparable.net),
                })}
            </p>

            {/* LES DEUX FORMES CÔTE À CÔTE, avec ce que chacune met de côté pour
                la retraite : c'est la ligne que les comparatifs oublient. */}
            <IndiceExplication lang={lang} />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {cartes.map((carte) => (
                    <div
                        key={carte.cle}
                        className={`rounded-xl border px-4 py-4 ${
                            gagnant === carte.cle
                                ? "bg-primary/10 border-primary/40"
                                : "border-gray-200/70 dark:border-white/10"
                        }`}
                    >
                        <div className="font-semibold dark:text-white">{carte.nom}</div>
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">{carte.partage}</div>
                        <div className="mt-3 text-xs text-gray-500 dark:text-lightBlue/70">{c.reste}</div>
                        <div className="font-bold text-xl tabular-nums dark:text-white">
                            <MontantExplicable
                                explication={explications[carte.cle]}
                                ouvert={ouverte === carte.cle}
                                onBasculer={() => basculer(carte.cle)}
                            >
                                <Montant valeur={Math.max(0, Math.round(carte.point.net))} lang={lang} reduit={reduit} />
                            </MontantExplicable>
                            <span className="text-xs font-normal text-gray-500 dark:text-lightBlue/70"> {c.parAn}</span>
                        </div>
                        {ouverte === carte.cle && <PanneauExplication explication={explications[carte.cle]} lang={lang} />}
                        <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-lightBlue">
                            <li className="flex gap-2">
                                <SolarIcon name="calendar-linear" className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                                {selonTrimestres(carte.point.trimestres, c.trimestres)}
                            </li>
                            <li className="flex gap-2">
                                <SolarIcon name="safe-square-linear" className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                                {remplir(c.caisses, { montant: sommes(carte.point.retraite) })}
                            </li>
                        </ul>
                    </div>
                ))}
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">
                {remplir(
                    // « La vraie différence est ailleurs » ne se dit que
                    // lorsqu'elle existe : mille euros d'écart au moins.
                    Math.abs(eurlPoint.retraite - sasuComparable.retraite) >= 1000
                        ? r.retraite
                        : r.retraiteProche,
                    {
                    eurl: sommes(eurlPoint.retraite),
                    sasu: sommes(sasuComparable.retraite),
                    },
                )}
            </p>
            {sasu.toutDividendes.net > 0 && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {remplir(r.sansSalaire, {
                        net: sommes(sasu.toutDividendes.net),
                        ecart: sommes(Math.abs(ecartSansSalaire)),
                        sens: ecartSansSalaire >= 0 ? r.plus : r.moins,
                    })}
                </p>
            )}

            <CourbeEcart serie={serie} enveloppe={enveloppe} textes={r.courbe} lang={lang} />

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.autres.titre}</div>
                <Liste items={r.autres.items} />
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={r.hypotheses.items} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">
                {r.avertissement}
            </p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
